import { SPENDING, CHECKING, SAVINGS } from '../../utils';

export const ONE_TIME_FREQUENCY_TYPE = 'OT';
export const RECURRING_FREQUENCY_TYPE = 'RC';
export const PRIMARY_ACCOUNT_TYPE = 'Primary';

export const FREQUENCY_TRANSLATION_KEYS = {
    "One Time": "one_time",
    "Recurring": "recurring",
    "Weekly": "weekly",
    "Biweekly": "biweekly",
    "Monthly": "monthly",
    "Quarterly": "quarterly"
};

export const FREQUENCY_DISPLAY_ORDER = ["One Time", "Weekly", "Biweekly", "Monthly", "Quarterly"];

export const RECURRING_FALLBACK_TYPE_MAP = {
    Weekly: 'WK',
    Biweekly: 'BW',
    Monthly: 'MN',
    Quarterly: 'QT'
};

const KLOGO_ACCOUNT_TYPES = [SPENDING, CHECKING, SAVINGS];

export const ACH_DISCLAIMER_TYPES = {
    internal: 'internal',
    achOneTime: 'achOneTime',
    achRecurring: 'achRecurring'
};

const isExternalAccount = (account) => {
    const achAccountInternalId = account?.ACHAccountInternalId;
    return achAccountInternalId !== undefined && achAccountInternalId !== null && Number(achAccountInternalId) !== 0;
};

/**
 * Determines which ACH disclaimer applies to a transfer.
 * Internal transfers (both accounts owned within Kudzu) get no disclaimer, while
 * external ACH transfers get either the one-time or the recurring variant.
 */
export const getAchDisclaimerType = (fromAccount, toAccount, frequencyType) => {
    const isExternalAchTransfer = isExternalAccount(fromAccount) || isExternalAccount(toAccount);
    if (!isExternalAchTransfer) return ACH_DISCLAIMER_TYPES.internal;
    return frequencyType === ONE_TIME_FREQUENCY_TYPE ? ACH_DISCLAIMER_TYPES.achOneTime : ACH_DISCLAIMER_TYPES.achRecurring;
};

/**
 * Resolves which logo asset represents the given account.
 * Returns `logos.fallback` when the account type is unknown or missing.
 */
export const resolveAccountLogo = (account, logos = {}) => {
    const { klogoGreen, primaryCard, fallback = null } = logos;
    const accountTypeName = account?.AccountTypeName ?? account?.AccountType;

    if (KLOGO_ACCOUNT_TYPES.includes(accountTypeName)) return klogoGreen;
    if (accountTypeName === PRIMARY_ACCOUNT_TYPE) return primaryCard;
    return fallback;
};

/**
 * Translates a frequency display text, falling back to the raw text
 * when no translation key is registered for it.
 */
export const getFrequencyLabel = (displayText, translate) =>
    FREQUENCY_TRANSLATION_KEYS[displayText] ? translate(FREQUENCY_TRANSLATION_KEYS[displayText]) : displayText;

/**
 * Builds the frequency dropdown options: "One Time" first, followed by the
 * recurring frequencies in display order. Recurring entries are matched to the
 * API response by display text; unmatched entries fall back to a locally
 * defined frequency type so the option is never dropped from the list.
 */
export const buildTransferFrequencyOptions = (recurringFrequencyArray = [], translate) => {
    const recurringFrequencyByName = (recurringFrequencyArray || []).reduce((acc, item) => {
        const key = item?.displayText || item?.DisplayText;
        if (key) acc[key] = item;
        return acc;
    }, {});

    const recurringOptions = FREQUENCY_DISPLAY_ORDER
        .filter((name) => name !== "One Time")
        .map((name) => recurringFrequencyByName[name] || { displayText: name, type: RECURRING_FALLBACK_TYPE_MAP[name] })
        .map((option) => ({ ...option, label: getFrequencyLabel(option.displayText || option.DisplayText, translate) }));

    return [
        { displayText: "One Time", type: ONE_TIME_FREQUENCY_TYPE, label: translate("one_time") },
        ...recurringOptions
    ];
};

/**
 * A recurring transfer always needs an end date, otherwise an invalid date is
 * sent to the transfer API. One time transfers never require one.
 */
export const isRecurringEndDateMissing = (frequencyType, endDate) =>
    frequencyType === RECURRING_FREQUENCY_TYPE && !endDate;

/**
 * Resolves the From/To dropdown option lists after the counter accounts for the
 * just-selected field are returned by the API. The field the user selected keeps
 * the full account list so reopening it still shows every account; only the
 * opposite field is narrowed to the eligible counter accounts.
 */
export const resolveTransferAccountOptions = (field, counterAccounts = [], allAccounts = [], currentOptions = {}) => {
    const { from = [], to = [] } = currentOptions;
    const hasAllAccounts = (allAccounts || []).length > 0;

    if (field === 'from') {
        return { from: hasAllAccounts ? allAccounts : from, to: counterAccounts || [] };
    }
    if (field === 'to') {
        return { from: counterAccounts || [], to: hasAllAccounts ? allAccounts : to };
    }
    return { from, to };
};
